import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import axios from "axios";
import Cookies from "js-cookie";
import DatePickerComponent from "../datepicker/DatePicker";
import '../../styles/analytics/analytics.css';
import ExportAnalyticsButton from '../buttons/ExportToExcel';

// Modal Component for Daily Records
const DailyRecordsModal = ({ isOpen, onClose, userData, activeTab, statusFilter, startDate, endDate }) => {
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (isOpen && userData) {
      fetchDailyData();
    }
  }, [isOpen, userData, startDate, endDate]);

  const fetchDailyData = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = activeTab === "scanners"
        ? `${baseUrl}/api/users/scanner-operators/${userData.id}/daily-analytics`
        : `${baseUrl}/api/index/indexers/${userData.id}/daily-analytics`;

      // Build query parameters
      const params = {};
      if (startDate && endDate) {
        // Range selected upstream: fetch each day within the range
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      // If no date range is selected, fetch all records (no date filter)

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        params,
      });
      
      // Assuming the API returns daily data with dates
      setDailyData(response.data || []);
    } catch (error) {
      setError(`Failed to load daily analytics for ${userData.name}`);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getFilteredDailySeries = () => {
    const dates = dailyData.map(item => formatDate(item.date));
    const daysOfWeek = dailyData.map(item => getDayOfWeek(item.date));
    
    switch (statusFilter) {
      case "approved":
        return {
          xAxisData: dates,
          series: [
            {
              name: 'Approved Acts',
              type: 'bar',
              stack: 'approved',
              data: dailyData.map(item => item.approvedActs ?? 0),
              itemStyle: { color: '#078ECE' },
              barWidth: 20,
            },
            {
              name: 'Approved Register',
              type: 'bar',
              stack: 'approved',
              data: dailyData.map(item => item.approvedRegister ?? 0),
              itemStyle: { color: '#F5A623' },
              barWidth: 20,
            },
            {
              name: 'Approved Yellow Forms',
              type: 'bar',
              stack: 'approved',
              data: dailyData.map(item => item.approvedYellowForms ?? 0),
              itemStyle: { color: '#10B981', barBorderRadius: [6, 6, 0, 0] },
              barWidth: 20,
            }
          ]
        };
      case "pending":
        return {
          xAxisData: dates,
          series: [
            {
              name: 'Pending Acts',
              type: 'bar',
              stack: 'pending',
              data: dailyData.map(item => item.pendingActs ?? 0),
              itemStyle: { color: '#DADADA' },
              barWidth: 20,
            },
            {
              name: 'Pending Register',
              type: 'bar',
              stack: 'pending',
              data: dailyData.map(item => item.pendingRegister ?? 0),
              itemStyle: { color: '#FFB74D' },
              barWidth: 20,
            },
            {
              name: 'Pending Yellow Forms',
              type: 'bar',
              stack: 'pending',
              data: dailyData.map(item => item.pendingYellowForms ?? 0),
              itemStyle: { color: '#A7F3D0', barBorderRadius: [6, 6, 0, 0] },
              barWidth: 20,
            }
          ]
        };
      default:
        return { xAxisData: [], series: [] };
    }
  };

  const getLegendData = () => {
    switch (statusFilter) {
      case "approved":
        return ['Approved Acts', 'Approved Register', 'Approved Yellow Forms'];
      case "pending":
        return ['Pending Acts', 'Pending Register', 'Pending Yellow Forms'];
      default:
        return [];
    }
  };

  if (!isOpen) return null;

  const { xAxisData, series } = getFilteredDailySeries();

  const modalOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#eee',
      borderWidth: 1,
      textStyle: { color: '#25282C', fontFamily: 'Jost, sans-serif' },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        const dataIndex = params[0].dataIndex;
        const date = xAxisData[dataIndex];
        const dayOfWeek = getDayOfWeek(dailyData[dataIndex]?.date);
        let tooltip = `<b>${dayOfWeek}, ${date}</b><br/>`;
        params.forEach(item => {
          tooltip += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
        });
        return tooltip;
      }
    },
    legend: {
      data: getLegendData(),
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: '#25282C', fontSize: 12, fontFamily: 'Jost, sans-serif' },
      align: 'auto',
    },
    grid: {
      left: 40,
      right: 40,
      top: 40,
      bottom: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: { 
        fontSize: 12, 
        color: '#25282C80', 
        fontFamily: 'Jost, sans-serif',
        interval: 0,
        rotate: 45
      },
      axisLine: { show: true, lineStyle: { type: 'solid', color: '#25282C80', width: 1 } },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 12, color: '#25282C80', fontFamily: 'Jost, sans-serif' },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#E5E7EB', type: 'dashed' } }
    },
    series: series,
    barCategoryGap: '20%'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '800px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Modal Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: 600, 
            color: '#222'
          }}>
            Daily Records - {userData?.name}
            {startDate && endDate && (
              <span style={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#666',
                marginLeft: '8px'
              }}>
                ({startDate.toLocaleDateString()}
                {startDate.toDateString() !== endDate.toDateString() && ` - ${endDate.toLocaleDateString()}`})
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div style={{ height: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              color: '#666'
            }}>
              Loading daily records...
            </div>
          ) : error ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#ef4444', 
              padding: '40px'
            }}>
              {error}
            </div>
          ) : dailyData.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#6C6C6C', 
              padding: '40px'
            }}>
              No daily data available for this {activeTab === "scanners" ? "scanner operator" : "indexer"}
              {startDate && endDate && ` between ${startDate.toLocaleDateString()} and ${endDate.toLocaleDateString()}`}.
            </div>
          ) : (
            <ReactECharts
              option={modalOption}
              style={{ height: '100%', width: '100%' }}
              notMerge={true}
              lazyUpdate={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AnalyticsChart = ({ activeTab = "scanners" }) => {
  const [chartData, setChartData] = useState([]);
  const [startDate, setStartDate] = useState(new Date("2024-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRangeTouched, setDateRangeTouched] = useState(false);
  const [statusFilter, setStatusFilter] = useState("approved");
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Dates are shown in the pickers by default but only applied as a filter
  // once the user actually changes one of them.
  const effectiveStartDate = dateRangeTouched ? startDate : null;
  const effectiveEndDate = dateRangeTouched ? endDate : null;

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setDateRangeTouched(true);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setDateRangeTouched(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = activeTab === "scanners"
          ? `${baseUrl}/api/users/scanner-operators/analytics`
          : `${baseUrl}/api/index/indexers/analytics`;

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          params: {
            startDate: effectiveStartDate ? effectiveStartDate.toISOString().split("T")[0] : undefined,
            endDate: effectiveEndDate ? effectiveEndDate.toISOString().split("T")[0] : undefined,
          },
        });

        const data = response.data?.map((item) => ({
          ...item, // Include all original properties including id
          name: item.name,
          approvedActs: item.approvedActs ?? 0,
          approvedRegister: item.approvedRegister ?? 0,
          approvedYellowForms: item.approvedYellowForms ?? 0, 
          pendingActs: item.pendingActs ?? 0,
          pendingRegister: item.pendingRegister ?? 0,
          pendingYellowForms: item.pendingYellowForms ?? 0, 
          rejectedActs: item.rejectedActs ?? 0,
          rejectedRegister: item.rejectedRegister ?? 0,
          rejectedYellowForms: item.rejectedYellowForms ?? 0,
        }));
        setChartData(data || []);
        setError(null);
      } catch (error) {
        setError(`Failed to load ${activeTab === "scanners" ? "scanner operators" : "indexers"} analytics data`);
        setChartData([]);
      }
    };
    fetchData();
  }, [effectiveStartDate, effectiveEndDate, activeTab, baseUrl]);

  // Handle bar click
  const handleBarClick = (params) => {
    const clickedUserIndex = params.dataIndex;
    const userData = chartData[clickedUserIndex];
    setSelectedUser(userData);
    setModalOpen(true);
  };

  // Get first name only for better display
  const getFirstName = (fullName) => {
    return fullName ? fullName.split(' ')[0] : 'Unknown';
  };

  const fullNames = chartData.map(item => item.name);
  const firstNames = chartData.map(item => getFirstName(item.name));
  const approvedActs = chartData.map(item => item.approvedActs);
  const approvedRegister = chartData.map(item => item.approvedRegister);
  const approvedYellowForms = chartData.map(item => item.approvedYellowForms);
  const pendingActs = chartData.map(item => item.pendingActs);
  const pendingRegister = chartData.map(item => item.pendingRegister);
  const pendingYellowForms = chartData.map(item => item.pendingYellowForms);
  const rejectedActs = chartData.map(item => item.rejectedActs);
  const rejectedRegister = chartData.map(item => item.rejectedRegister);
  const rejectedYellowForms = chartData.map(item => item.rejectedYellowForms);

  // Filter data based on status
  const getFilteredSeries = () => {
    switch (statusFilter) {
      case "approved":
        return [
          {
            name: 'Approved Acts',
            type: 'bar',
            stack: 'approved',
            data: approvedActs,
            itemStyle: { color: '#078ECE'},
            barWidth: 20,
          },
          {
            name: 'Approved Register',
            type: 'bar',
            stack: 'approved',
            data: approvedRegister,
            itemStyle: { color: '#F5A623'},
            barWidth: 20,
          },
          {
            name: 'Approved Yellow Forms',
            type: 'bar',
            stack: 'approved',
            data: approvedYellowForms,
            itemStyle: { color: '#10B981', barBorderRadius: [6, 6, 0, 0] },
            barWidth: 20,
          }
        ];
      case "pending":
        return [
          {
            name: 'Pending Acts',
            type: 'bar',
            stack: 'pending',
            data: pendingActs,
            itemStyle: { color: '#DADADA'},
            barWidth: 20,
          },
          {
            name: 'Pending Register',
            type: 'bar',
            stack: 'pending',
            data: pendingRegister,
            itemStyle: { color: '#FFB74D'},
            barWidth: 20,
          },
          {
            name: 'Pending Yellow Forms',
            type: 'bar',
            stack: 'pending',
            data: pendingYellowForms,
            itemStyle: { color: '#A7F3D0', barBorderRadius: [6, 6, 0, 0] },
            barWidth: 20,
          }
        ];
      case "rejected":
        return [
          {
            name: 'Rejected Acts',
            type: 'bar',
            stack: 'rejected',
            data: rejectedActs,
            itemStyle: { color: '#EF4444'},
            barWidth: 20,
          },
          {
            name: 'Rejected Register',
            type: 'bar',
            stack: 'rejected',
            data: rejectedRegister,
            itemStyle: { color: '#F87171'},
            barWidth: 20,
          },
          {
            name: 'Rejected Yellow Forms',
            type: 'bar',
            stack: 'rejected',
            data: rejectedYellowForms,
            itemStyle: { color: '#FCA5A5', barBorderRadius: [6, 6, 0, 0] },
            barWidth: 20,
          }
        ];  
      default:
        return [];
    }
  };

  const getLegendData = () => {
    switch (statusFilter) {
      case "approved":
        return ['Approved Acts', 'Approved Register', 'Approved Yellow Forms'];
      case "pending":
        return ['Pending Acts', 'Pending Register', 'Pending Yellow Forms'];
      case "rejected":
        return ['Rejected Acts', 'Rejected Register', 'Rejected Yellow Forms'];
      default:
        return [];
    }
  };

  const getTitle = () => {
    return activeTab === "scanners" ? "Scanner Operators" : "Indexers";
  };

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#eee',
      borderWidth: 1,
      textStyle: { color: '#25282C', fontFamily: 'Jost, sans-serif'},
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        const dataIndex = params[0].dataIndex;
        const fullName = fullNames[dataIndex];
        let tooltip = `<b>${fullName}</b><br/>`;
        params.forEach(item => {
          tooltip += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
        });
        tooltip += '<br/><i style="color: #666; font-size: 12px;">Click to view daily records</i>';
        return tooltip;
      }
    },
    legend: {
      data: getLegendData(),
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: '#25282C', fontSize: 14, fontFamily: 'Jost, sans-serif' },
      align: 'auto',
    },
    grid: {
      left: 40,
      right: 40,
      top: 60,
      bottom: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: firstNames,
      axisLabel: { 
        fontSize: 14, 
        color: '#25282C80', 
        fontFamily: 'Jost, sans-serif',
        interval: 0,
        rotate: 45
      },
      axisLine: { show: true, lineStyle: { type: 'solid', color: '#25282C80', width: 1 } },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 15, color: '#25282C80', fontFamily: 'Jost, sans-serif' },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#E5E7EB', type: 'dashed' } }
    },
    series: getFilteredSeries(),
    barCategoryGap: '25%'
  };

  return (
    <>
      <div className="analytics-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, marginTop: 8 }}>
          <h2 style={{ fontWeight: 600, fontSize: 20, color: "#222", margin: 0 }}>
            {getTitle()}
          </h2>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            {/* Export Button */}
          <ExportAnalyticsButton
            activeTab={activeTab}
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
          />
            <div className="analytics-filter" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: 14, color: "#666", fontWeight: 500 }}>Filter:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  color: "#333",
                  cursor: "pointer",
                  minWidth: '100px'
                }}
              >
                <option value="approved" style={{ paddingInline: '1rem' }}>Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="date-picker-container" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <DatePickerComponent selectedDate={startDate} onDateChange={handleStartDateChange} search={true} />
              <span style={{ color: "#666", fontSize: 14 }}>to</span>
              <DatePickerComponent selectedDate={endDate} onDateChange={handleEndDateChange} isEndDate={true} startDate={startDate} />
            </div>
          </div>
        </div>

        {/* Add click instruction */}
        <p style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginBottom: '16px', 
          fontStyle: 'italic' 
        }}>
          Click on any bar to view daily records for that {activeTab === "scanners" ? "scanner operator" : "indexer"}
        </p>

        {chartData.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6C6C6C", marginBlock: "100px" }}>
            No data available for the selected date range.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <ReactECharts
              option={option}
              className="analytics-chart"
              style={{ height: 400, width: Math.max(600, firstNames.length * 45) }}
              notMerge={true}
              lazyUpdate={true}
              onEvents={{
                'click': handleBarClick
              }}
            />
          </div>
        )}
        {error && (
          <p style={{ textAlign: "center", color: "red" }}>{error}</p>
        )}
      </div>

      {/* Daily Records Modal */}
      <DailyRecordsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        userData={selectedUser}
        activeTab={activeTab}
        statusFilter={statusFilter}
        startDate={effectiveStartDate}
        endDate={effectiveEndDate}
      />
    </>
  );
};

export default AnalyticsChart;
